import { db, storage } from "./firebase";
import { collection, addDoc, onSnapshot, deleteDoc, doc, query, where, serverTimestamp, getDoc, updateDoc, writeBatch } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid';

export const onNodesChange = (projectId, parentId, callback) => {
    const nodesRef = collection(db, 'projects', projectId, 'nodes');
    const q = query(nodesRef, where("parentId", "==", parentId));
    
    return onSnapshot(q, (snapshot) => {
        const nodes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const sortedNodes = nodes.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
                return a.order - b.order;
            }
            if (a.order !== undefined) return -1;
            if (b.order !== undefined) return 1;
            
            if (a.type === 'folder' && b.type !== 'folder') return -1;
            if (a.type !== 'folder' && b.type === 'folder') return 1;
            return a.name.localeCompare(b.name);
        });

        callback(sortedNodes);
    }, (error) => {
        console.error("Error listening to node changes:", error);
    });
};

export const addNode = (projectId, nodeData, userId) => {
    const nodesRef = collection(db, 'projects', projectId, 'nodes');
    return addDoc(nodesRef, {
        ...nodeData,
        projectId,
        createdBy: userId,
        createdAt: serverTimestamp()
    });
};

export const deleteNode = async (projectId, nodeId) => {
    const nodeDocRef = doc(db, 'projects', projectId, 'nodes', nodeId);
    const nodeSnap = await getDoc(nodeDocRef);

    if (nodeSnap.exists()) {
        const nodeData = nodeSnap.data();

        if (nodeData.type === 'file' && nodeData.url && nodeData.fileType !== 'placeholder/floorplan') {
            try {
                // Create a ref from the full URL
                const fileRef = ref(storage, nodeData.url);
                await deleteObject(fileRef);
            } catch (error) {
                if (error.code === 'storage/object-not-found') {
                    console.log("File not found in storage. Deleting reference from Firestore.");
                } else {
                    console.error("Error deleting file from storage:", error);
                    throw error; 
                }
            }
        }
        
        // TODO: Add recursive delete for folders
        await deleteDoc(nodeDocRef);

    } else {
        console.error("Node not found for deletion");
        throw new Error("Node not found");
    }
};

export const updateNodeDetails = (projectId, nodeId, data) => {
    const nodeDocRef = doc(db, 'projects', projectId, 'nodes', nodeId);
    return updateDoc(nodeDocRef, data);
};

export const updateNodesOrder = async (projectId, orderedIds) => {
    if (!orderedIds || orderedIds.length === 0) return;

    const batch = writeBatch(db);
    orderedIds.forEach((id, index) => {
        const docRef = doc(db, 'projects', projectId, 'nodes', id);
        batch.update(docRef, { order: index });
    });
    return await batch.commit();
};


export const uploadFileAndUpdateNode = (projectId, nodeId, file, progressCallback) => {
    return new Promise((resolve, reject) => {
        const filePath = `projects/${projectId}/files/${uuidv4()}-${file.name}`;
        const storageRef = ref(storage, filePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                if (progressCallback) progressCallback(progress);
            },
            (error) => {
                console.error("Upload failed:", error);
                reject(error);
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    const fileData = {
                        name: file.name,
                        url: downloadURL,
                        size: file.size,
                        fileType: file.type,
                    };
                    await updateNodeDetails(projectId, nodeId, fileData);
                    resolve();
                } catch (error) {
                    console.error("Failed to update file node in Firestore:", error);
                    reject(error);
                }
            }
        );
    });
};


export const uploadFile = (projectId, parentId, file, userId, progressCallback) => {
    return new Promise((resolve, reject) => {
        const filePath = `projects/${projectId}/files/${uuidv4()}-${file.name}`;
        const storageRef = ref(storage, filePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                progressCallback(progress);
            },
            (error) => {
                console.error("Upload failed:", error);
                reject(error);
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    const fileNode = {
                        name: file.name,
                        type: 'file',
                        parentId: parentId,
                        url: downloadURL,
                        size: file.size,
                        fileType: file.type,
                    };
                    await addNode(projectId, fileNode, userId);
                    resolve();
                } catch (error) {
                    console.error("Failed to create file node in Firestore:", error);
                    reject(error);
                }
            }
        );
    });
};
